import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { SportEvent, SportEventApiGetSportEventsRequest } from 'videoservice-openapi-react';
import { EEventQueryKey, eventApi } from 'entities/event';
import { IUseEventsFilterOptions } from './use-events-filter.types';

export const useEventsFilter = ({ sportIds }: IUseEventsFilterOptions) => {
  const [searchValue, setSearchValue] = useState('');
  const [eventList, setEventList] = useState<SportEvent[]>([])
  const [debouncedSearchValue] = useDebounceValue(searchValue, 500);

  const searchQuery = useMemo(() => debouncedSearchValue.trim(), [debouncedSearchValue]);
  const isNumber = useMemo(() => /^\d+$/.test(searchQuery), [searchQuery]);
  const isIdle = useMemo(() =>
    !searchValue || !searchQuery || (!isNumber && searchQuery.length < 3),
    [searchValue, searchQuery, isNumber]
  );

  const fetchEvents = useCallback(async () => {
    const params: SportEventApiGetSportEventsRequest = {
      searchQuery: isNumber ? undefined : searchQuery,
      sportEventIds: isNumber ? [Number(searchQuery)] : undefined,
      sportIds: isNumber ? undefined : sportIds,
    };

    const result = await eventApi.getSportEvents(params);
    const eventList = result.items;

    setEventList(eventList);

    return eventList;
  }, [searchQuery, isNumber, sportIds]);

  useEffect(() => {
    if (!searchQuery) {
      setEventList([]);
    }
  }, [searchQuery])

  const query = useQuery({
    enabled: !isIdle,
    queryKey: [
      EEventQueryKey.EVENTS,
      searchQuery,
    ],
    queryFn: fetchEvents,
    retry: false,
  });

  const isLoading = useMemo(() => query.isLoading ||
    !isIdle && query.isFetching, [isIdle, query.isFetching, query.isLoading]);

  const isNotFound = useMemo(() => !eventList.length &&
    !isIdle && query.isFetched &&
    !!searchQuery, [eventList.length, isIdle, query.isFetched, searchQuery]);

  return {
    query,
    searchValue,
    debouncedSearchValue,
    setSearchValue,
    eventList,
    isNumber,
    isLoading,
    isNotFound,
    isIdle,
  };
};
